import type { ExtractFileRequest, FileAnalytic } from '@/features/file-management/services/AnalyticService';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UploadFile } from 'antd/es/upload';
import dayjs from 'dayjs';

export interface FileProcessResponse {
    file_upload: FileAnalytic;
    task: FileAnalyticStatus;
}

export interface UploadFileStatus {
    file: UploadFile;             // file gốc
    uploadDate: dayjs.Dayjs;           // thời điểm upload (ISO string từ dayjs)
    uploadResponse?: {
        success: boolean,
        fileId: string,
        cloudLink: string,
        id: string
    };         // response sau khi gọi uploadFileApiWithProgress
    uploadStatus?: FileProcessResponse;           // response sau khi gọi extractFileRequest + getTaskByGuidFile
    uploadProcess?: FileAnalyticStatus;
    progress: number;
}
export interface FileAnalyticStatus {
    _id: string;
    irai_id: string;
    guid_file: string;
    type: string;
    sum_page: number;
    current_page: number;
    status: string;
    verify: boolean;
}

interface UploadState {
    files: UploadFile[];
    fileStatusList: UploadFileStatus[];
    currentStep: number;
    processing: boolean;
    loading: boolean;
    fileAnalytic: FileProcessResponse | null;
    fileStatus: FileAnalyticStatus | null;
    error: string | null;
}

const initialState: UploadState = {
    files: [],
    fileStatusList: [],
    currentStep: 0,
    processing: false,
    loading: false,
    fileAnalytic: null,
    fileStatus: null,
    error: null,
};

const uploadSlice = createSlice({
    name: 'upload',
    initialState,
    reducers: {
        setFiles(state, action: PayloadAction<{ fileList: UploadFile[], companyId: string, labelId: string, isPublish: boolean }>) {
            const now = dayjs();
            state.files = action.payload.fileList;
            action.payload.fileList.forEach(file => {
                const exists = state.fileStatusList.find(d => d.file.uid === file.uid);
                if (!exists) {
                    state.fileStatusList.push({
                        file,
                        uploadDate: now,
                        progress: 0
                    });
                }
            });
        },
        setFileStatusList(state, action: PayloadAction<UploadFileStatus[]>) {
            state.fileStatusList = action.payload;
        },
        setCurrentStep(state, action: PayloadAction<number>) {
            state.currentStep = action.payload;
        },
        setProcessing(state, action: PayloadAction<boolean>) {
            state.processing = action.payload;
        },
        resetUpload(state) {
            // Chỉ reset UI/modal, KHÔNG xóa fileStatusList
            // state.files = [];
            state.currentStep = 0;
            state.processing = false;
        },
        updateFileStatus(state, action: PayloadAction<UploadFileStatus>) {
        },
        updateFileProgress(state, action: PayloadAction<{ fileName: string; progress: number }>) {
            const idx = state.fileStatusList.findIndex(fs => fs.file.name === action.payload.fileName);
            if (idx !== -1) state.fileStatusList[idx].progress = action.payload.progress;
        },
        cancelUploading(state, action: PayloadAction<string>) { },
        cancelProcessing(state, action: PayloadAction<string>) { },
        clearEmptyFileItems(state) {
            state.fileStatusList = state.fileStatusList.filter(item =>
                item.file && item.file.name && item.file.size !== undefined
            );
        },
        extractFileRequest: (state, _action: PayloadAction<ExtractFileRequest>) => {
            state.loading = true;
            state.error = null;
        },
        extractFileSuccess: (state, action: PayloadAction<FileProcessResponse>) => {
            state.loading = false;
            state.fileAnalytic = action.payload;
        },
        extractFileFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
        getFileStatusRequest: (state, action: PayloadAction<string>) => {
            state.loading = true;
            state.error = null;
        },
        getFileStatusSuccess: (state, action: PayloadAction<FileAnalyticStatus>) => {
            state.loading = false;
            state.fileStatus = action.payload;
        },
        getFileStatusFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
        setUploadResponse: (state, action: PayloadAction<{ uid: string; response: any }>) => {
            const item = state.fileStatusList.find(d => d.file.uid === action.payload.uid);
            if (item) {
                item.uploadResponse = action.payload.response;
            }
        },
        setUploadStatus: (state, action: PayloadAction<{ guid_file: string; status: FileProcessResponse }>) => {
            const item = state.fileStatusList.find(d =>
                d.uploadResponse && d.uploadResponse.fileId === action.payload.guid_file
            );
            if (item) {
                item.uploadStatus = action.payload.status;
            }
        },
        setUploadProcess: (state, action: PayloadAction<{ guid_file: string; process: FileAnalyticStatus }>) => {
            const item = state.fileStatusList.find(d =>
                d.uploadResponse && d.uploadResponse.fileId === action.payload.guid_file
            );
            if (item) {
                item.uploadProcess = action.payload.process;
            }
        },
        processCompleted: (state, action: PayloadAction<{ guid_file: string; status: 'success' | 'failed' }>) => {
            // optional: có thể update state tại đây
        },
    },
});

export const {
    setFiles,
    setFileStatusList,
    setCurrentStep,
    setProcessing,
    resetUpload,
    updateFileStatus,
    updateFileProgress,
    cancelUploading,
    cancelProcessing,
    clearEmptyFileItems,
    extractFileRequest,
    extractFileSuccess,
    extractFileFailure,
    getFileStatusRequest,
    getFileStatusSuccess,
    getFileStatusFailure,
    setUploadResponse,
    setUploadStatus,
    setUploadProcess,
    processCompleted
} = uploadSlice.actions;
export const uploadActions = uploadSlice.actions;
export const uploadReducer = uploadSlice.reducer;